import React from 'react';
import { faArrowCircleUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { AllBrandsFilterSection } from './components/AllBrandsFilterSection';
import TopPicksSection from './components/TopPicksSection';
import { ContentfulView } from './components/ContentfulView';

import { FeaturesState } from 'common/features/featuresReducer';
import { useRootSelector } from 'rootStore';
import { createStyles } from 'utils/createStyle';
import { ShopScreen } from 'features/shop-base/ShopScreen';
import { HomepageContext, HomepageViewProps } from 'features/homepage/contexts/contentful';
import { HeroSection } from 'features/homepage/sections';
import Skeleton from 'react-loading-skeleton';
import { useRemoteContent } from 'hooks/useRemoteContent';
import { selectLoginState } from 'common/features/store/duck/account/duck';
import { SearchBar } from 'components/search/SearchBar';
import { updateSearch, applyTerm } from 'common/features/store/duck/search/duck';
import { Routes, getPath } from 'routes';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import SuggestionsBySearchTerm from './components/SuggestionsBySearchTerm';
import EmptyResults from './EmptyResults';
import { ConstructorRefinedContent } from './ConstructorRefinedContent';
import { EarnTypeConstructorFilter } from 'common/api/constructor/util';
import { firebaseValueAsString, useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import { selectIsConstructorBrowseEnabledWeb } from 'common/features/store/duck/ffs';
import { defaultConfigKeys } from 'common/modules/remote-config/defaultConfigKeys';

const styles = createStyles({
    main: [
        '',
        {
            gridTemplateColumns: '18rem auto',
        },
    ],
    sectionStyles: ['mx-4 lg:mx-16 2xl:mx-28'],
    filterSection: ['', { margin: '0 !important' }],
    gridCommandsContainer: [
        'grid w-full mb-4',
        {
            gridTemplateColumns: 'auto max-content',
        },
    ],
    bonusSection: [
        'grid',
        {
            gridTemplateColumns: 'auto max-content',
            marginBottom: '50px',
        },
    ],
    featuredSection: [
        'bg-pale-blue',
        {
            height: '437px',
            marginBottom: '50px',
        },
    ],
    topPicksSection: [
        'mb-12',
        {
            marginBottom: '50px',
        },
    ],
    scrollToTop: [
        'cursor-pointer flex flex-col items-center justify-evenly bg-grey-accent text-brand font-semibold text-center',
        {
            width: '42px',
            height: '60px',
            borderRadius: '5px 0px 0px 5px',
            boxShadow: '0px 2px 4px -1px rgb(0 0 0 / 6%), 0px 4px 6px -1px rgb(0 0 0 / 10%)',
            position: 'fixed',
            bottom: '10%',
            fontSize: 12,
            right: 0,
        },
    ],
    searchBackgroundStyle: [
        'w-full bg-white mb-6 shadow-inner',
        {
            minHeight: 150,
        },
    ],
});

const HeroId: { [key: string]: string } = {
    [Routes.ShopGiftCards]: 'auth_giftcards_page_hero_id',
    [Routes.ShopOnline]: 'auth_shoponline_page_hero_id',
    [Routes.Brands]: 'auth_brands_page_hero_id',
};

export interface SearchResultsScreenProps {
    earnType?: EarnTypeConstructorFilter;
    singleTypeView?: boolean;
}

export const SearchResultsScreen = ({ earnType, singleTypeView }: SearchResultsScreenProps) => {
    const isConstructorEnabled = useRootSelector(selectIsConstructorBrowseEnabledWeb);
    const total = useRootSelector((s: FeaturesState) => s.store.searchGrid.total);
    const isUserLoggedIn = useRootSelector(selectLoginState);
    const history = useHistory();
    const dispatch = useDispatch<any>();
    const { pathname } = useLocation();
    const matchedPath = matchPath(pathname, Object.keys(HeroId));
    const headerId = (
        !isUserLoggedIn ? 'unauth_brands_page_hero_id' : matchedPath ? HeroId[matchedPath.path] : ''
    ) as keyof typeof defaultConfigKeys;
    const { heroes, loading: isRemoteContentLoading, heroId } = useRemoteContent(headerId);

    const affiliateErrorMessage = useFirebaseConfigValues(
        'affiliate_error_message_web',
        null,
        firebaseValueAsString
    );
    const hideFilter = earnType === 'Online' && singleTypeView && affiliateErrorMessage;

    const searchTerm = useRootSelector((s: FeaturesState) => s.store.search.appliedTerm);

    const headerContext: HomepageViewProps = {
        isAuthenticated: false,
        layout: [],
        heroes: heroes,
        componentMap: {},
        isLoading: isRemoteContentLoading,
    };

    /**
     * prerender override documented here
     * https://docs.prerender.io/docs/25-empty-or-partially-rendered-pages
     *
     * We're setting prerenderReady as false at the document level
     * And in order for prerender to capture the /brands page
     * we need to manually set it to true here (along with the unauthed PDP)
     */
    setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).prerenderReady = true;
    }, 5000);

    const scrollToTop = () =>
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
        });

    const showResultsPage = Boolean(
        total === null || (total as number) > 0 || (!searchTerm && !total)
    );

    const showFilters: boolean = showResultsPage || Boolean(total);

    const applySearchTerm = (term: string) => {
        dispatch(applyTerm(term));
    };

    const navigateToProductDetailPage = (item: Brand) => {
        if (item.id) {
            dispatch(updateSearch({ term: item.name }));
            if (item.isLocalProduct) {
                const [, , id] = item.id.split('-');
                history.push(getPath(Routes.ShopLocalProductDetails, { productId: id }));
            } else {
                const idWithSlug = item.urlSlug
                    ? `${item.id}-${item.urlSlug.replace(/\//g, '')}`
                    : item.id;
                history.push(getPath(Routes.BrandsDetails, { id: idWithSlug }));
            }
        }
    };

    const SearchBarBrands = () => (
        <section className="w-full">
            <div className="relative">
                <div className={styles.searchBackgroundStyle} />
                <div className="absolute top-0 bottom-0 right-0 left-0 flex flex-col items-center justify-center font-light">
                    <div className=" w-11/12 md:w-4/6 flex items-center">
                        <SearchBar
                            className="w-full md:w-4/6 md:max-w-2xl ml-auto mr-auto"
                            onItemSelected={navigateToProductDetailPage}
                            onSearch={applySearchTerm}
                        />
                    </div>
                </div>
            </div>
        </section>
    );

    return (
        <ShopScreen>
            <ContentfulView>
                <HomepageContext.Provider value={headerContext}>
                    {isRemoteContentLoading || !heroes[heroId] ? (
                        <Skeleton className="h-80" />
                    ) : (
                        showResultsPage && <HeroSection id={heroId} />
                    )}
                </HomepageContext.Provider>

                {!isUserLoggedIn ? <SearchBarBrands /> : null}

                {isUserLoggedIn && showResultsPage ? <ConstructorRefinedContent /> : null}
                <main className={styles.main}>
                    <>
                        <section
                            id="filter"
                            className={`${styles.sectionStyles} ${styles.filterSection}`}
                        >
                            <AllBrandsFilterSection
                                visible={!hideFilter && showFilters && showResultsPage}
                                earnType={earnType}
                            />
                        </section>
                    </>

                    {showResultsPage ? (
                        <>
                            <TopPicksSection earnType={earnType} singleTypeView={singleTypeView} />

                            <div onClick={scrollToTop} className={styles.scrollToTop}>
                                <FontAwesomeIcon size="2x" icon={faArrowCircleUp} />
                                Top
                            </div>
                        </>
                    ) : (
                        <div className={isUserLoggedIn ? 'mt-14 sm:mt-20' : ''}>
                            {isConstructorEnabled ? (
                                <EmptyResults isConstructorEnabled term={searchTerm} />
                            ) : (
                                <SuggestionsBySearchTerm searchTerm={searchTerm} />
                            )}
                        </div>
                    )}
                </main>
            </ContentfulView>
        </ShopScreen>
    );
};
